import React, { useContext, useState, ChangeEvent, useEffect, useCallback } from "react";
import {
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Media,
  Navbar,
  Nav,
  Container,
  Dropdown,
  NavItem
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { AppContext } from "services/appContext/AppContext";
import { FormattedMessage, useIntl } from "react-intl";
import { IdentityService } from "services/identity/IdentityService";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";
import { SpIdProfileDto } from "services/apis/types/account/SpIdProfileDto";
import NotificationsDropdown from "components/framework/adminLayout/NotificationsDropdown";
import classnames from "classnames";
import Select2Dropdown from "./Select2Dropdown";
import { EnvironmentUtil } from "services/util/EnvironmentUtil";
import { v4 as uuidv4 } from 'uuid';
import FeedbackButton from "components/networkExplorer/common/FeedbackButton";
import { AppContextAccessor } from "services/appContext/AppContextAccessor";
import { useDropdownDisabled } from "services/customHooks/useDropdownDisabled";

const isMixNetworkUrl = EnvironmentUtil.isMixNetwork;
const isqaUrl = EnvironmentUtil.isqa;
const istestUrl = EnvironmentUtil.istest;
const isdevUrl = EnvironmentUtil.isdev;
const isCspireUrl = EnvironmentUtil.isCspire;
const isMixQAUrl = EnvironmentUtil.isMixQa;
const isMixUatUrl = EnvironmentUtil.isMixUat;
const isPrdUrl = EnvironmentUtil.isPrdUrl;
const isPrdMixUrl = EnvironmentUtil.isPrdMixUrl;

type Props = {
  toggleSidenav: () => void;
  sidenavOpen: boolean;
};

export default function AdminNavbar(props: Props) {
  const { appContext, setAppContext } = useContext(AppContext);
  const isDropdownDisabled = useDropdownDisabled();
  const theme = appContext.theme;

  const addUniqueIds = (records) => {
    return records.map((record) => {
      // Check if the record already has an ID in local storage
      if (!record.spId || !record.internalId) {
        return { id: uuidv4(), ...record }; 
      }
      const storedId = localStorage.getItem(`recordId_${record.spId}_${record.internalId}`);
      const id = storedId || uuidv4();
            // Save the ID in local storage for future reference
      localStorage.setItem(`recordId_${record.spId}_${record.internalId}`, id);

      return { id, ...record };
    });
  };
  const initializeId = (record) => {
    // Retrieve the ID from local storage
    if (!record || !record.spId || !record.internalId) {
      return "";
    }
    const storedId = localStorage.getItem(`recordId_${record.spId}_${record.internalId}`);
    return storedId || "";
  };
  const recordsWithUniqueIds = appContext.localStorageInfo.user?.spIdProfiles ? addUniqueIds(appContext.localStorageInfo.user?.spIdProfiles) : [];
  const initializingIdwithSelectedSpidProfile: any = appContext.localStorageInfo.selectedProfile ? initializeId(appContext.localStorageInfo.selectedProfile) : "";
  // Call the function to add unique IDs to the records
  const [id, setId] = useState<string>(initializingIdwithSelectedSpidProfile);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const intl = useIntl();
  const history = useHistory();

  useEffect(() => {
    if (appContext.localStorageInfo.selectedProfile) {
      const newId = initializeId(appContext.localStorageInfo.selectedProfile);
      if (newId !== id) {  // Only update if the newId is different from current id
        setId(newId);
      }
    }
  }, [appContext.localStorageInfo.selectedProfile, id]); // Add `id` as a dependency to avoid unnecessary re-renders  

  const dropdownToggle = () => setDropdownOpen((prevState) => !prevState);
  const logout = () => IdentityService.clearAuthenticationInfo();

 const selectSpidHandler = (spidInfo: SpIdProfileDto) => {
  const spIdProfile = recordsWithUniqueIds.find((x) => x.id === spidInfo.id);

  if (spIdProfile) {
    // Check if the profile id matches
    if (spIdProfile.id !== id) {
      setId(spidInfo.id); // Only set if ID changes
      setAppContext({
        ...appContext,
        localStorageInfo: { ...appContext.localStorageInfo, selectedProfile: spIdProfile }
      });
      showInfoNotification(
        `${intl.formatMessage(
          { id: "adminNavbar.newProfileSelected" },
          { spId: profileText(spIdProfile) }
        )}`
      );
      setDropdownOpen(false);
    } 
  }
};
  const getBannerTitle = () => {
    if (isMixUatUrl) return "Banners.uat.title";
    if (isMixQAUrl) return "Banners.qa.title";
    if (istestUrl) return "Banners.uat.title";
    if (isdevUrl) return "Banners.dev.title";
    if (isqaUrl) return "Banners.qa.title";
    
    return null;
  };

  const profileText = (profile: SpIdProfileDto) => {
    if (!isMixNetworkUrl){
      return profile.nonSpIdCompany ? profile.name : `${profile.name} (${profile.spId})`;
    }
    else{
      return profile.internalId ? `${profile.name} (${profile.internalId})` : `${profile.name}`; 
    }
  };

  const wholeSaleUserSpidText = appContext.localStorageInfo.selectedProfile && !appContext.localStorageInfo.selectedProfile.nonSpIdCompany
  ? appContext.localStorageInfo.selectedProfile.internalId && appContext.localStorageInfo.selectedProfile.internalId !== "null"
    ? `(${appContext.localStorageInfo.selectedProfile.internalId})`
    : ""
  : "";

  const userSpidText = appContext.localStorageInfo.selectedProfile && !appContext.localStorageInfo.selectedProfile.nonSpIdCompany
    ? ` (${
      appContext.localStorageInfo.selectedProfile &&
      appContext.localStorageInfo.selectedProfile.spId
    })`
    : "";

  const spIdText = !isMixNetworkUrl ? userSpidText : wholeSaleUserSpidText ;

  const changeTheme = (newTheme) => {
    AppContextAccessor.setAppContext((appContext) => {
      return {
        ...appContext,
        theme: newTheme
      }
    });
  };

  // Clear outdated local storage keys 
  useEffect(() => {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("recordId_") && !key.includes("undefined"))
      .forEach((key) => {
        if (!recordsWithUniqueIds.some((record) => key === `recordId_${record.spId}_${record.internalId}`)) {
          localStorage.removeItem(key);
        }
      });
  }, [recordsWithUniqueIds]);

  return (
    <Navbar className="navbar navbar-expand navbar-top navbar-dark pc-nav-border-bottom pc-bg-gradient-primary">
      <Container fluid={true} className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <Nav className="align-items-center d-xl-none" navbar>
            <NavItem>
              <div
                className={classnames("pr-3 sidenav-toggler sidenav-toggler-dark", {
                  active: props.sidenavOpen
                })}
                onClick={props.toggleSidenav}>
                <div className="sidenav-toggler-inner">
                  <i className={`sidenav-toggler-line ${theme === "light" ? "sidenav-toggler-line-light" : ""}`} />
                  <i className={`sidenav-toggler-line ${theme === "light" ? "sidenav-toggler-line-light" : ""}`} />
                  <i className={`sidenav-toggler-line ${theme === "light" ? "sidenav-toggler-line-light" : ""}`} />
                </div>
              </div>
            </NavItem>
          </Nav>
        </div>
        {!isCspireUrl && getBannerTitle() && (
          <h1 className={`display-3 ${theme === "light" ? "lblue-color" : "pc-number-explorer-bg"}`}>
            <FormattedMessage id={getBannerTitle() as string} />
          </h1>
        )}
        <Nav className="align-items-center ml-auto ml-md-0" navbar>
          <Dropdown nav isOpen={dropdownOpen} toggle={dropdownToggle}>
            <DropdownToggle className="nav-link pr-0 cursor-pointer" color="" tag="a">
              <Media className="align-items-center">
                <Media className="ml-2 d-none d-lg-block">
                  <span className={`mb-0 text-sm font-weight-bold ${theme === "light" ? "lblue-color" : ""}`}>
                    {appContext.localStorageInfo.authenticationInfo &&
                      `${appContext.localStorageInfo.authenticationInfo.username} - ${appContext.localStorageInfo.selectedProfile?.name} ${spIdText}`}
                  </span>
                </Media>
                <i className={`ml-2 fa fa-user fa-lg ${theme === "light" ? "purple-color" : ""}`} />
              </Media>
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem className="noti-title" header tag="div">
                <h6 className="text-overflow m-0">
                  <FormattedMessage id="adminNavbar.switchProfile" />
                </h6>
              </DropdownItem>
              {appContext.localStorageInfo.user &&
                appContext.localStorageInfo.user.spIdProfiles &&
                appContext.localStorageInfo.user.spIdProfiles.length > 0 && (
                  <div className="dropdown-item">
                    <Select2Dropdown
                    data={
                      recordsWithUniqueIds.map((x) => ({ id: x.id, text: profileText(x),
                      key: `${x.id}-${x.spId}`  
                    }))}
                    isWholeSaleClientType={isMixNetworkUrl}
                    handleInputChange={(event) => {
                      const selectedId = event.target.value;
                      const selectedOption = recordsWithUniqueIds.find((x) => x.id === selectedId);
                    // Ensure a selected option was found before calling selectSpidHandlers 
                      if (selectedOption) {
                        selectSpidHandler(selectedOption);
                      }                       
                    }}
                    value={id}
                    autofocus={true}
                    disabled={isDropdownDisabled}
                  />
                  </div>
                )}
              <DropdownItem onClick={() => history.push("/MyProfile")} className="cursor-pointer">
                <i className="ni ni-single-02" />
                <span>
                  <FormattedMessage id="adminNavbar.myProfile" />
                </span>
              </DropdownItem>
              {!isMixNetworkUrl && !isCspireUrl && (
                <DropdownItem className="cursor-pointer" onClick={() => history.push("/Support")}>
                  <i className="ni ni-support-16" />
                  <span>
                    <FormattedMessage id="adminNavbar.support" />
                  </span>
                </DropdownItem>
              )}
              <DropdownItem divider></DropdownItem>
              <DropdownItem onClick={() => logout()} className="cursor-pointer">
                <i className="ni ni-user-run" />
                <span>
                  <FormattedMessage id="adminNavbar.logout" />
                </span>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          <NotificationsDropdown />
          <FeedbackButton/>
        </Nav>
      </Container>
    </Navbar>
  );
}